import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import ThemeToggle from '../../../components/ThemeToggle';
import InvestigationTrigger from '../components/InvestigationTrigger';
import ProceduresManager from '../components/ProceduresManager';
import UserGuide from '../../../components/UserGuide';
import WhistleblowerPageEditor from '../components/WhistleblowerPageEditor';
import InvoiceArchive from './InvoiceArchive';
import SubscriptionPanel from '../components/SubscriptionPanel';
import SupportPanel from '../components/SupportPanel';
import InviteUserModal from '../components/InviteUserModal';
import AutomaticProceduresPanel from '../components/AutomaticProceduresPanel';
import DepartmentManagement from './DepartmentManagement';
import InvestigationList from '../components/InvestigationList';
import InvestigatorDirectory from '../components/InvestigatorDirectory';
import DeadlineOverview, { getDeadlineStatus } from '../components/DeadlineOverview';

const { 
  FiHome, FiFileText, FiUsers, FiSettings, FiBell, FiLogOut, FiPieChart, 
  FiSearch, FiMenu, FiX, FiPlus, FiChevronRight, FiPlay, FiBook, 
  FiUserPlus, FiDollarSign, FiHeadphones, FiBuilding, FiShield, FiClock, FiCheckCircle, FiAlertCircle
} = FiIcons;

const EnhancedCompanyDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { reports = [], companies = [], users: allUsers = [], departments, loading } = useData();
  const { investigations = [], notifications = [] } = useInvestigation();
  const [activeView, setActiveView] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Defensiv sjekk for selskap
  const company = companies.find(c => c.id === user?.companyId);
  const companyReports = reports.filter(r => r.companyId === user?.companyId || r.company_id === user?.companyId);
  const companyUsers = allUsers.filter(u => u.companyId === user?.companyId || u.company_id === user?.companyId);
  const activeNotifications = (notifications || []).filter(n => n.reportId && companyReports.find(r => r.id === n.reportId));

  const stats = {
    total: companyReports.length,
    new: companyReports.filter(r => r.status === 'new').length,
    active: companyReports.filter(r => ['investigating', 'assigned', 'in_progress'].includes(r.status)).length,
    closed: companyReports.filter(r => r.status === 'resolved' || r.status === 'closed').length
  };

  const menuItems = [
    { id: 'overview', label: 'Oversikt', icon: FiHome },
    { id: 'reports', label: 'Varsler', icon: FiFileText, badge: stats.new },
    { id: 'investigations', label: 'Utredninger', icon: FiShield, badge: (investigations || []).filter(i => i.status === 'in_progress').length },
    { id: 'directory', label: 'Finn Utreder', icon: FiSearch },
    { id: 'qr-code', label: 'Varslingsside', icon: FiPieChart },
    { id: 'procedures', label: 'Prosedyrer', icon: FiPlay },
    { id: 'guide', label: 'Veileder & Hjelp', icon: FiBook },
    { id: 'users', label: 'Brukere', icon: FiUserPlus },
    { id: 'departments', label: 'Avdelinger', icon: FiBuilding, hide: company?.subscription !== 'konsern' && (company?.departments_count || 0) <= 1 },
    { id: 'subscription', label: 'Abonnement & Faktura', icon: FiDollarSign },
    { id: 'support', label: 'Support', icon: FiHeadphones }
  ];

  const handleLogout = () => {
    logout();
    navigate('/company/login');
  };

  if (loading) return <div className="flex h-screen items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  if (!company) return (
    <div className="flex flex-col h-screen items-center justify-center bg-gray-50 p-8 text-center">
      <SafeIcon icon={FiShield} className="text-4xl text-gray-300 mb-4" />
      <h2 className="text-xl font-bold">Ingen tilgang</h2>
      <p className="text-gray-500 mt-2">Vi fant ikke din bedriftsprofil. Vennligst logg inn på nytt.</p>
      <button onClick={handleLogout} className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg font-bold">Til innlogging</button>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 font-sans text-gray-900 dark:text-gray-100 overflow-hidden">
      <InvestigationTrigger />
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out flex flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
              {company.name?.charAt(0)}
            </div>
            <div className="overflow-hidden">
              <h1 className="text-sm font-bold truncate">{company.name}</h1>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">{company.subscription}</p>
            </div>
          </div>
          <button className="md:hidden" onClick={() => setIsMobileMenuOpen(false)}>
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {menuItems.filter(i => !i.hide).map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveView(item.id); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${activeView === item.id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
            >
              <div className="flex items-center gap-3">
                <SafeIcon icon={item.icon} className={activeView === item.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'} />
                <span>{item.label}</span>
              </div>
              {item.badge > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100 dark:border-gray-700 space-y-1">
          <ThemeToggle />
          <button onClick={() => navigate('/company/settings')} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <SafeIcon icon={FiSettings} /> Innstillinger
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
            <SafeIcon icon={FiLogOut} /> Logg ut
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden relative">
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-16 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button className="md:hidden p-2 text-gray-600 dark:text-gray-300" onClick={() => setIsMobileMenuOpen(true)}>
              <SafeIcon icon={FiMenu} className="text-xl" />
            </button>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white hidden md:block">
              {menuItems.find(i => i.id === activeView)?.label}
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <button className="p-2 text-gray-400 hover:text-blue-600 relative">
              <SafeIcon icon={FiBell} className="text-xl" />
              {activeNotifications.length > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>}
            </button>
            <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 mx-2 hidden md:block"></div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden md:block">
                <p className="text-sm font-bold text-gray-900 dark:text-white leading-none">{user?.name || 'Bruker'}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 capitalize font-bold">{user?.role || 'Bruker'}</p>
              </div>
              <div className="w-9 h-9 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-300 font-bold border border-gray-200 dark:border-gray-600">
                {(user?.name?.charAt(0) || 'U').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 relative">
          <AnimatePresence mode="wait">
            {activeView === 'overview' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { label: 'Totalt', val: stats.total, icon: FiFileText, color: 'blue' },
                    { label: 'Nye', val: stats.new, icon: FiBell, color: 'red' },
                    { label: 'Pågår', val: stats.active, icon: FiClock, color: 'yellow' },
                    { label: 'Løst', val: stats.closed, icon: FiCheckCircle, color: 'green' }
                  ].map((s, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <div className="flex justify-between items-start mb-4">
                        <div className={`p-3 bg-${s.color}-50 dark:bg-${s.color}-900/30 rounded-xl text-${s.color}-600 dark:text-${s.color}-400`}>
                          <SafeIcon icon={s.icon} className="text-xl" />
                        </div>
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{s.label}</span>
                      </div>
                      <h3 className="text-3xl font-black text-gray-900 dark:text-white">{s.val}</h3>
                    </div>
                  ))}
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="font-bold text-gray-900 dark:text-white">Siste hendelser</h3>
                      <button onClick={() => setActiveView('reports')} className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline">Se alle</button>
                    </div>

                    <div className="space-y-4">
                      {companyReports.slice(0, 5).map(report => {
                        const dlStatus = getDeadlineStatus(report);
                        const isUrgent = dlStatus.startsWith('overdue') || dlStatus.startsWith('urgent');
                        
                        return (
                          <div key={report.id} onClick={() => navigate(`/company/report/${report.id}`)} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors cursor-pointer group">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${report.severity === 'high' || report.severity === 'critical' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                                <SafeIcon icon={report.type === 'hms_incident' ? FiIcons.FiAlertTriangle : FiFileText} />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-800 dark:text-white text-sm group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{report.title}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-tight">{new Date(report.createdAt || report.created_at).toLocaleDateString()} • {report.reporterName || 'Anonym'}</p>
                                  {isUrgent && (
                                    <span className="flex items-center gap-1 text-[9px] font-black bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded uppercase">
                                      <SafeIcon icon={FiAlertCircle} size={10} /> {dlStatus.includes('ack') ? 'Bekreftelse' : 'Frist'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <SafeIcon icon={FiChevronRight} className="text-gray-300 dark:text-gray-600 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                          </div>
                        );
                      })}
                      {companyReports.length === 0 && <p className="text-center py-10 text-gray-400 italic">Ingen varsler registrert ennå.</p>}
                    </div>
                  </div>

                  <div className="space-y-6">
                    <DeadlineOverview reports={companyReports} />
                    <AutomaticProceduresPanel />
                  </div>
                </div>
              </motion.div>
            )}

            {activeView === 'reports' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold">Innkomne Varsler</h2>
                  </div>
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-[10px] font-black uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                      <tr>
                        <th className="px-6 py-4">Varsel</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Alvorlighet</th>
                        <th className="px-6 py-4">Dato</th>
                        <th className="px-6 py-4 text-right">Handling</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {companyReports.map(r => {
                        const dlStatus = getDeadlineStatus(r);
                        const isUrgent = dlStatus.startsWith('overdue') || dlStatus.startsWith('urgent');

                        return (
                          <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer" onClick={() => navigate(`/company/report/${r.id}`)}>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-sm">{r.title}</p>
                                {isUrgent && <SafeIcon icon={FiAlertCircle} className="text-red-500 animate-pulse" size={14} />}
                              </div>
                              <p className="text-[10px] text-gray-400 font-bold uppercase">{r.category || r.type}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${r.status === 'new' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                {r.status || 'Ny'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`font-bold text-xs uppercase ${r.severity === 'critical' ? 'text-red-600' : 'text-gray-600'}`}>{r.severity}</span>
                            </td>
                            <td className="px-6 py-4 text-xs font-bold text-gray-500">
                              {new Date(r.createdAt || r.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button className="text-blue-600 font-bold text-xs uppercase hover:underline">Se sak</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {activeView === 'investigations' && <InvestigationList />}
            {activeView === 'directory' && <InvestigatorDirectory />}
            {activeView === 'qr-code' && <WhistleblowerPageEditor />}
            {activeView === 'procedures' && <ProceduresManager />}
            {activeView === 'guide' && <UserGuide userType="company" isEmbedded={true} />}

            {activeView === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Brukere</h2>
                  <button onClick={() => setShowInviteModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2">
                    <SafeIcon icon={FiUserPlus} /> Inviter bruker
                  </button>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-2xl border dark:border-gray-700 overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-[10px] font-black uppercase text-gray-500 border-b dark:border-gray-700">
                      <tr><th className="px-6 py-4">Navn</th><th className="px-6 py-4">E-post</th><th className="px-6 py-4">Rolle</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {companyUsers.map(u => (
                        <tr key={u.id} className="text-sm">
                          <td className="px-6 py-4 font-bold">{u.name}</td>
                          <td className="px-6 py-4 text-gray-500">{u.email}</td>
                          <td className="px-6 py-4 capitalize font-medium">{u.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <InviteUserModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />
              </div>
            )}

            {activeView === 'departments' && <DepartmentManagement />}

            {activeView === 'subscription' && (
              <div className="space-y-12">
                <SubscriptionPanel />
                <InvoiceArchive isEmbedded={true} />
              </div>
            )}

            {activeView === 'support' && <SupportPanel />}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default EnhancedCompanyDashboard;